import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import { Header, Loader, SideBar } from "components/common";
import { LiveCallsIcon } from "assets";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { setAccountId } from "../../../redux/slices/api.slice";
import { useInitialLoader } from "hooks/useInitialLoader";
import Cookies from "js-cookie";
import { useAppDispatch } from "../../../redux/hooks";

import "./defaultLayout.scss";
import { useLocation } from "react-router-dom";
import { APP_METADATA } from "../../../constants";

interface DefaultLayoutProps {
  children: ReactNode;
}

const DefaultLayout: React.FC<DefaultLayoutProps> = ({ children }) => {
  const { logout } = useInitialLoader();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const sessionData = useSelector((state: RootState) => state.session.sessionData);
  const accounts = useSelector((state: RootState) => state.accounts.accounts);
  
  const [selectedAccount, setSelectedAccount] = useState<string | null>(Cookies.get(`last_account_id`) || null);

  const currentAppKey = useMemo(() => {
  const path = location.pathname.split('/').filter(Boolean)[0];
  return path || 'live-calls'; 
}, [location.pathname]);


  const appMeta = useMemo(()=>{
    return(
      APP_METADATA.find((app)=>app.key === currentAppKey) || {
        title:'Unknown App',
        icon:null
      }
    )
  },[currentAppKey])

  // Sync selected account from sessionStorage or session
  useEffect(() => {
  if (!sessionData?.accountID) return;

  const cookieAccountId = Cookies.get("last_account_id");
  const finalAccountId = cookieAccountId || sessionData.accountID;

  setSelectedAccount(finalAccountId);
}, [sessionData?.accountID]);


  // Dispatch account ID to redux and save to sessionStorage
  useEffect(() => {
    if (selectedAccount) {
      dispatch(setAccountId(selectedAccount));
    }
  }, [selectedAccount]);


  // Guard rendering until session + accounts are ready
  const isReady =
    !!sessionData &&
    !!sessionData.permissions &&
    !!selectedAccount &&
    accounts.length > 0;

  if (!isReady) {
    return <div className="default-layout-loading"><Loader /></div>;
  }

  return (
    <div className="default-layout-container">
      <SideBar
        appName={currentAppKey}
        appMenu={[]}
        permissions={sessionData.permissions as unknown as { [key: string]: string[] }}
        accounts={accounts || []}
        userAccount={selectedAccount}

        selectedAccountId={selectedAccount}
        setSelectedAccountId={setSelectedAccount}
      />
      <main className="content-area">
        <Header
          logout={logout}
          name={`${sessionData?.firstName} ${sessionData?.lastName}`}
          email={sessionData?.email || ""}
          icon={appMeta.icon}
          title={appMeta.title}
        />
        <div className="content">{children}</div>
      </main>
    </div>
  );
};

export default DefaultLayout;
